import { useEffect, useState } from 'react';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Iconify from '../../../components/Iconify';
import { styled } from '@mui/material/styles';
import { decryptString } from '../../../utils/url';
import { useAppDispatch, useAppSelector } from '../../../store';
import Loader from '../../../components/Loader';
import { Users } from '../../../store/users/api/interfaces';
import { getUser } from '../../../store/users/api';
import Label from '../../../components/Label';
import WalletTransactionTab from './WalletTransactionsTab';
import OrderHistoryTab from './OrderHistoryTab';
import WishlistTab from './WishlistTab';
import RewardTab from './RewardTab';
import ReferralTab from './ReferralTab';
import { getInferredNationalityLabel } from '../../../utils/nationalityInference';
import useToggle from '../../../hooks/useToggle';

type Params = {
  id: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Grid mt={1}>{children}</Grid>}
    </div>
  );
}

const IconStyle = styled(Iconify)(({ theme }) => ({
  width: 20,
  height: 20,
  marginTop: 1,
  flexShrink: 0,
  marginRight: theme.spacing(2),
}));

const index = () => {
  const dispatch = useAppDispatch();
  const params = useParams<Params>();
  const navigation = useNavigate();
  const [user, setUser] = useState<Users>();
  const [tabValue, setTabValue] = useState<number>(0);
  const { isLoading } = useAppSelector((state) => state.user);
  const {
    toggle: editCreditToggle,
    onOpen: onOpenEditCredit,
    onClose: onCloseEditCredit,
  } = useToggle();

  const decodeParam = decryptString(params.id ?? '');
  useEffect(() => {
    const fetchById = async () => {
      try {
        const record = await dispatch(getUser({ id: decodeParam })).unwrap();
        setUser(record);
      } catch (error: any) {
        navigation('/users');
      }
    };
    fetchById();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Page title={'View Customer'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'View Customer'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Users', href: '/users' },
          ]}
        />
        <Card>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <CardHeader title={'Details'} />
              <Grid
                container
                spacing={2}
                mb={3}
                ml={1}
                mr={1}
                mt={1}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:person-outline'} />
                    <Typography variant='subtitle2'>Name:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{user?.firstName} {user?.lastName}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:phone-outline'} />
                    <Typography variant='subtitle2'>Phone:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{user?.countryCallingCode}-{user?.mobileNumber}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:email-outline'} />
                    <Typography variant='subtitle2'>Email:</Typography>
                    <Typography variant='body2'>&nbsp;{user?.email}</Typography>
                  </Stack>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:toggle-left-outline'} />
                    <Typography variant='subtitle2'>Active Status:</Typography>
                    <Typography variant='body2'>
                      &nbsp;
                      <Label
                        variant='ghost'
                        color={user?.activeState ? 'success' : 'error'}
                      >
                        {user?.activeState ? 'Active' : 'InActive'}
                      </Label>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:briefcase-outline'} />
                    <Typography variant='subtitle2'>Balance:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{user?.balance}
                    </Typography>
                  </Stack>
                </Grid>   
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <Stack direction='row'>
                    <IconStyle icon={'eva:globe-outline'} />
                    <Typography variant='subtitle2'>Nationality:</Typography>
                    <Typography variant='body2'>
                      &nbsp;{getInferredNationalityLabel(user)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </>
          )}
        </Card>

        <Card sx={{ my: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: 2,
              mt: 2,
              flexWrap: 'wrap',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons={true}
              aria-label='scrollable auto tabs example'
              sx={{ minHeight: 48 }}
            >
              <Tab label={'Order History'} />
              <Tab label={'Wallet Transactions'} />
              <Tab label={'Wishlist'} /> 
              <Tab label={'Reward'} />
              <Tab label={'Referral'} />
            </Tabs>
            {tabValue === 1 && (
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={onOpenEditCredit}
                sx={{ mb: 0.5 }}
              >
                {'Edit Credit'}
              </Button>
            )}
          </Box>

          <TabPanel
            value={tabValue}
            index={0}
          >
            <OrderHistoryTab user={user} />
          </TabPanel>
          <TabPanel
            value={tabValue}
            index={1}
          >
            <WalletTransactionTab
              user={user}
              toggle={editCreditToggle}
              onOpen={onOpenEditCredit}
              onClose={onCloseEditCredit}
              onBalanceUpdated={(balance) =>
                setUser((prev) =>
                  prev ? { ...prev, balance: String(balance) } : prev
                )
              }
            />
          </TabPanel>
          <TabPanel
            value={tabValue}
            index={2}
          >
            <WishlistTab user={user} />
          </TabPanel>
          <TabPanel
            value={tabValue}
            index={3}
          >
            <RewardTab user={user} />
          </TabPanel>
          <TabPanel
            value={tabValue}
            index={4}
          >
            <ReferralTab user={user} />
          </TabPanel>
        </Card>
      </Container>
    </Page>
  );
};

export default index;
